import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"

// Document-level hotkeys for the editor. Ignores events while user is typing
// in inputs/textareas/contenteditable so form submits, text editing, etc.
// don't get eaten.
export default class extends Controller {
  connect() {
    this._onKey = (e) => this.#handle(e)
    // Reset the arrow-nudge coalesce window whenever the selection changes,
    // so a fresh press on a newly-selected object always opens its own
    // undo entry instead of silently joining the previous object's burst.
    this._onSelection = () => { this._lastNudgeAt = null }
    window.addEventListener("keydown", this._onKey)
    editorStore.addEventListener("selection:change", this._onSelection)
  }

  disconnect() {
    window.removeEventListener("keydown", this._onKey)
    editorStore.removeEventListener("selection:change", this._onSelection)
  }

  undo() { editorStore.undo() }
  redo() { editorStore.redo() }

  #handle(e) {
    if (isTyping(e.target)) return
    // While a full-screen modal editor is open (house floor-plan), the site
    // editor must not act on keys — otherwise Delete deletes the selected
    // house, Cmd+Z rolls back site state, arrows nudge it, etc. The overlay
    // has its own key handling.
    if (document.body.classList.contains("house-mode-open")) return
    const mod = e.metaKey || e.ctrlKey

    if (e.key === "Delete" || e.key === "Backspace") {
      if (editorStore.selectedId) {
        editorStore.removeObject(editorStore.selectedId)
        e.preventDefault()
      }
    } else if (mod && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
      editorStore.undo()
      e.preventDefault()
    } else if (mod && ((e.key === "z" || e.key === "Z") && e.shiftKey || e.key === "y" || e.key === "Y")) {
      editorStore.redo()
      e.preventDefault()
    } else if (mod && (e.key === "d" || e.key === "D")) {
      if (editorStore.selectedId) {
        editorStore.duplicateObject(editorStore.selectedId)
        e.preventDefault()
      }
    } else if (e.key === "Escape") {
      editorStore.selectObject(null)
    } else if (e.key.startsWith("Arrow")) {
      this.#nudge(e)
    }
  }

  // Bambu-Studio-style precision nudging: arrow keys move selection 0.1 m,
  // Shift+arrow = 1 m. Mod+arrow is reserved for browser text-navigation and
  // never hijacked. We coalesce rapid presses into one undo entry via a 500ms
  // idle-reset window.
  #nudge(e) {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const id = editorStore.selectedId
    if (!id) return
    const obj = editorStore.objects.find((o) => o.id === id)
    if (!obj) return
    const step = e.shiftKey ? 1 : 0.1
    let dx = 0, dy = 0
    if (e.key === "ArrowLeft") dx = -step
    else if (e.key === "ArrowRight") dx = step
    else if (e.key === "ArrowUp") dy = -step
    else if (e.key === "ArrowDown") dy = step
    else return
    e.preventDefault()

    const now = (typeof performance !== "undefined" ? performance.now() : Date.now())
    const coalesce = this._lastNudgeAt && (now - this._lastNudgeAt) < 500
    this._lastNudgeAt = now

    editorStore.updateObject(id, {
      xM: round3(obj.xM + dx),
      yM: round3(obj.yM + dy),
    }, { snapshot: !coalesce })
  }
}

function round3(n) {
  return Math.round(n * 1000) / 1000
}

function isTyping(el) {
  if (!el) return false
  const tag = el.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable
}
